/**
 * Home "Announcements" (Oct 2026, Falco's feedback; 23 Oct: personal blocks, images, placeholders): the live
 * admin-authored notices for this person that have the Home channel, newest first, rendered for them by
 * `announcementsForMe`. "Got it" hides one for this user (dismissedAnnouncements key `ann:<id>`); the post
 * otherwise disappears after its last day.
 */
import { useState } from 'react';
import { Megaphone } from 'lucide-react';
import { announcementDismissKey, type User as UserDoc } from '@daaily/shared';
import { card, sectionTitle } from '../design/ui';
import { Icon } from '../design/Icon';
import { callDismissAnnouncement } from '../announcements/callables';
import { AnnouncementBody, useMyAnnouncements, type MyAnnouncement } from '../announcements/AnnouncementView';

export { invalidateAnnouncements } from '../announcements/AnnouncementView';

export function AnnouncementsCard({ user }: { user: UserDoc }) {
  const list = useMyAnnouncements();
  const [hidden, setHidden] = useState<Record<string, boolean>>({});

  const dismissed = new Set(user.dismissedAnnouncements ?? []);
  const visible = (list ?? []).filter((a) => a.channels.home && !dismissed.has(announcementDismissKey(a.id)) && !hidden[a.id]);
  if (visible.length === 0) return null;

  const gotIt = (a: MyAnnouncement) => {
    setHidden((h) => ({ ...h, [a.id]: true }));
    callDismissAnnouncement({ key: announcementDismissKey(a.id) }).catch(() => {
      setHidden((h) => { const n = { ...h }; delete n[a.id]; return n; });
    });
  };
  const fmt = (iso: string) => new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });

  return (
    <section aria-labelledby="announcements-h" className="space-y-3 lg:col-start-1">
      <h2 id="announcements-h" className={sectionTitle}>Announcements</h2>
      <ul className="space-y-3">
        {visible.map((a) => (
          <li key={a.id} className={`${card} p-4`}>
            <div className="flex items-start gap-3">
              <span className="mt-0.5 text-brand-solid"><Icon as={Megaphone} size={18} /></span>
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-semibold text-fg">{a.rendered.title}</h3>
                <p className="text-xs text-fg-faint mb-2">{a.authorDisplayName} · {fmt(a.publishAt)} · until {fmt(a.expiresAt)}</p>
                <AnnouncementBody rendered={a.rendered} linkUrl={a.linkUrl} linkLabel={a.linkLabel} />
                <div className="mt-3">
                  <button type="button" onClick={() => gotIt(a)} className="text-sm text-fg-faint hover:text-fg" title="Hide this announcement for you">
                    Got it
                  </button>
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
